import React from "react";
import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

interface LanguageOption {
  code: string;
  label: string;
  flag: string;
}

const LANGUAGES: LanguageOption[] = [
  {
    code: "en-US",
    label: "English",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='512' height='512' fill='%23bd3d44'/%3E%3Cpath stroke='%23fff' stroke-width='40' d='M0 58h512M0 138h512M0 218h512M0 298h512M0 378h512M0 458h512'/%3E%3Crect width='204.8' height='275.2' fill='%23192f5d'/%3E%3C/svg%3E",
  },
  {
    code: "pl-PL",
    label: "Polski",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='512' height='256' y='256' fill='%23dc143c'/%3E%3Crect width='512' height='256' fill='%23fff'/%3E%3C/svg%3E",
  },
  {
    code: "de-DE",
    label: "Deutsch",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='512' height='170.7' fill='%23000'/%3E%3Crect width='512' height='170.7' y='170.7' fill='%23d00'/%3E%3Crect width='512' height='170.6' y='341.3' fill='%23ffce00'/%3E%3C/svg%3E",
  },
  {
    code: "es-ES",
    label: "Español",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='512' height='128' y='0' fill='%23c60b1e'/%3E%3Crect width='512' height='256' y='128' fill='%23ffc400'/%3E%3Crect width='512' height='128' y='384' fill='%23c60b1e'/%3E%3C/svg%3E",
  },
  {
    code: "fr-FR",
    label: "Français",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='170.7' height='512' fill='%230055a4'/%3E%3Crect width='170.6' height='512' x='170.7' fill='%23fff'/%3E%3Crect width='170.7' height='512' x='341.3' fill='%23ef4135'/%3E%3C/svg%3E",
  },
  {
    code: "uk-UA",
    label: "Українська",
    flag: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Crect width='512' height='256' fill='%230057b7'/%3E%3Crect width='512' height='256' y='256' fill='%23ffd700'/%3E%3C/svg%3E",
  },
];

interface LanguageSwitcherProps {
  /** Visual variant for different contexts */
  variant?: "default" | "dark" | "minimal";
  /** Show current language label next to icon */
  showLabel?: boolean;
  /** Additional class names */
  className?: string;
  /** Only show these language codes (e.g., ["en-US", "pl-PL"]) */
  limitLanguages?: string[];
}

/**
 * Language switcher dropdown component.
 * Can be used in admin topbar, onboarding wizard, or standalone pages.
 * Changing language preserves React state (form data won't be lost).
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = "default",
  showLabel = false,
  className,
  limitLanguages,
}) => {
  const { i18n } = useTranslation();

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];
  const availableLanguages = limitLanguages
    ? LANGUAGES.filter((l) => limitLanguages.includes(l.code))
    : LANGUAGES;

  const buttonVariant = variant === "dark" ? "ghost" : "ghost";
  const buttonClasses = cn(
    variant === "dark" && "text-white/70 hover:text-white hover:bg-white/10",
    variant === "minimal" && "h-8 w-8",
    className
  );

  const contentClasses = cn(
    variant === "dark" && "bg-slate-800 border-slate-700"
  );

  const itemClasses = cn(
    variant === "dark" && "text-slate-200 focus:bg-white/10 focus:text-white"
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={buttonVariant} size={showLabel ? "sm" : "icon"} className={buttonClasses}>
          <Languages className="h-5 w-5" />
          {showLabel && (
            <span className="ml-2 text-sm">{currentLang.label}</span>
          )}
          <span className="sr-only">Change language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={contentClasses}>
        {availableLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            className={cn(
              itemClasses,
              i18n.language === lang.code && "bg-accent"
            )}
          >
            <img
              src={lang.flag}
              alt={`${lang.label} flag`}
              className="mr-2 h-4 w-4 inline-block"
            />
            <span>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
